import { AxiosReadPayload } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-payload';
import { axiosReadHelper } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-helper';
import { AxiosResultStatus } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-result-status';

export type AvailityVobOverrides = {
  fname?: string;
  lname?: string;
  dob?: string;
  memberId?: string;
  pcode?: string;
  npi?: string;
};

export type FacilityWithNpi = {
  facilityId: number;
  npi: string;
};

/** GET clients/v2/client/facility/{id} — finpass-ui only (no shared-platform change). */
async function getFacilityById(facilityId: number): Promise<AxiosResultStatus> {
  const payload: AxiosReadPayload = {
    dataId: -2,
    url: `clients/v2/client/facility/${facilityId}`,
  };
  return axiosReadHelper(payload);
}

/**
 * First facility (list order) with a non-empty NPI from the facility API.
 * Used only by Patient-tab Run Availity.
 */
export async function findFirstFacilityWithValidNpi(
  facilities: { facilityId: number }[] | undefined | null
): Promise<FacilityWithNpi | null> {
  if (!facilities?.length) {
    return null;
  }
  for (const facility of facilities) {
    const facilityId = Number(facility.facilityId);
    if (!Number.isInteger(facilityId) || facilityId <= 0) {
      continue;
    }
    const response = await getFacilityById(facilityId);
    if (response?.hasErrors || !response?.entity) {
      continue;
    }
    const npi = String(
      (response.entity as { npi?: string | null }).npi ?? ''
    ).trim();
    if (npi) {
      return { facilityId, npi };
    }
  }
  return null;
}

/** GET availity/vob with optional demographics / NPI query overrides. */
export async function getAvailityVobWithOverrides(
  patientId: number,
  facilityId: number,
  overrides?: AvailityVobOverrides
): Promise<AxiosResultStatus> {
  const params = new URLSearchParams({
    patientId: String(patientId),
    facilityId: String(facilityId),
  });
  if (overrides) {
    for (const key of ['fname', 'lname', 'dob', 'memberId', 'pcode', 'npi'] as const) {
      const value = overrides[key]?.trim();
      if (value) {
        params.set(key, value);
      }
    }
  }
  const payload: AxiosReadPayload = {
    dataId: -2,
    url: `availity/vob?${params.toString()}`,
  };
  return axiosReadHelper(payload);
}

export function formatAvailityDob(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
